/* The Yarn stock tab: five tiles, the total-stock line, closing by quality
 * on a chosen day, the count-by-count table, produced against dispatched,
 * and the register checks. All figures come from `build`. */

import { Blueprint, KpiRow, PanelHead } from '../../components/ui'
import { Frame, PANEL, RIGHT, ShareBar, Swatch, Tip } from './parts'
import type { Built } from './model'

export function YarnTab({ v, setAsOf, setHov }: {
  v: Built
  setAsOf: (d: string) => void
  setHov: (d: string | null) => void
}) {
  return (
    <>
      <KpiRow items={v.yKpis} />

      <Blueprint className="sheet" style={PANEL}>
        <PanelHead title="Total yarn stock over the month" hint={v.totSub} />
        <Frame height={240} ticks={v.totTicks} labels={v.trendLabels} onLeave={() => setHov(null)}>
          <svg viewBox="0 0 1000 240" preserveAspectRatio="none"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block' }}>
            <polygon points={v.totArea} style={{ fill: 'var(--color-accent-200)', opacity: 0.55 }} />
            <polyline points={v.totLine} fill="none" strokeWidth="2.2" strokeLinejoin="round"
              vectorEffect="non-scaling-stroke" style={{ stroke: 'var(--color-accent-700)' }} />
            {v.totHov ? (
              <line x1={v.totHov.x} y1="0" x2={v.totHov.x} y2="240" strokeWidth="1"
                vectorEffect="non-scaling-stroke" style={{ stroke: 'var(--color-neutral-700)', opacity: 0.5 }} />
            ) : null}
          </svg>
          {v.totDots.map((p, i) => (
            <span key={i} style={{
              position: 'absolute', left: p.left, bottom: p.bottom, width: p.size, height: p.size,
              transform: 'translate(-50%,50%)', borderRadius: '50%', background: 'var(--color-accent-700)',
              border: '2px solid var(--color-bg)', pointerEvents: 'none',
            }} />
          ))}
          <div style={{ position: 'absolute', inset: 0, display: 'flex' }}>
            {v.totCols.map((d) => (
              <div key={d} onMouseEnter={() => setHov(d)} style={{ flex: '1 1 0', height: '100%' }} />
            ))}
          </div>
          {v.totHov ? (
            <Tip left={v.totHov.left} shift={v.totHov.shift}>
              <div style={{ fontWeight: 600 }}>{v.totHov.title}</div>
              <div className="num" style={{ fontFamily: 'var(--font-heading)', fontSize: 22, marginTop: 2 }}>
                {v.totHov.value}
              </div>
              <div style={{ color: 'var(--color-neutral-700)', marginTop: 2 }}>{v.totHov.delta}</div>
            </Tip>
          ) : null}
        </Frame>
      </Blueprint>

      <Blueprint className="sheet" style={PANEL}>
        <div style={{
          display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap',
        }}>
          <div>
            <h2 style={{ fontSize: 22, margin: 0 }}>Closing yarn stock by quality</h2>
            <div style={{ fontSize: 12, color: 'var(--color-neutral-600)', marginTop: 4 }}>
              Stock as of <strong style={{ color: 'var(--color-text)' }}>{v.asOfLabel}</strong> · {v.asOfTotal} across {v.asOfCount} qualities
            </div>
          </div>
          <label className="no-print" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span className="axis-title" style={{ flex: 'none', whiteSpace: 'nowrap' }}>As of</span>
            <select value={v.asOf ?? ''} onChange={(e) => setAsOf(e.target.value)} style={{ minWidth: 150 }}>
              {v.asOfOptions.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
          </label>
        </div>
        <Frame height={280} ticks={v.qTicks}>
          <div style={{
            position: 'absolute', inset: 0, display: 'flex', alignItems: 'flex-end',
            justifyContent: 'space-around', gap: 14, padding: '0 14px',
          }}>
            {v.qBars.map((b) => (
              <div key={b.label} title={b.tip} style={{ position: 'relative', flex: '1 1 0', maxWidth: 72, height: b.h, background: b.bg }}>
                <span className="num" style={{
                  position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)', marginBottom: 4,
                  fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', color: 'var(--color-text)',
                }}>{b.v}</span>
              </div>
            ))}
          </div>
        </Frame>
        <div style={{ display: 'grid', gridTemplateColumns: '52px minmax(0,1fr)', gap: 8 }}>
          <div />
          <div style={{ display: 'flex', justifyContent: 'space-around', gap: 14, padding: '8px 14px 0' }}>
            {v.qBars.map((b) => (
              <div key={b.label} style={{ flex: '1 1 0', maxWidth: 72, textAlign: 'center' }}>
                <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>{b.label}</div>
                <div style={{ fontSize: 11, color: 'var(--color-neutral-600)' }}>{b.share}</div>
              </div>
            ))}
          </div>
        </div>
      </Blueprint>

      <Blueprint className="sheet" style={PANEL}>
        <PanelHead title="Count by count" hint={v.countSub} />
        <div style={{ overflowX: 'auto', marginTop: 14 }}>
          <table className="table num" style={{ minWidth: 820 }}>
            <thead>
              <tr>
                <th>Count</th>
                <th style={RIGHT}>Opening</th>
                <th style={RIGHT}>Produced</th>
                <th style={RIGHT}>Dispatched</th>
                <th style={RIGHT}>Issued / adj.</th>
                <th style={RIGHT}>Closing</th>
                <th style={RIGHT}>Change</th>
                <th style={RIGHT}>Days of cover</th>
                <th style={{ width: '18%' }}>Share of stock</th>
              </tr>
            </thead>
            <tbody>
              {v.countRows.map((r) => (
                <tr key={r.label}>
                  <td style={{ whiteSpace: 'nowrap', fontWeight: 600 }}><Swatch bg={r.bg} />{r.label}</td>
                  <td style={RIGHT}>{r.open}</td>
                  <td style={RIGHT}>{r.prod}</td>
                  <td style={RIGHT}>{r.disp}</td>
                  <td style={RIGHT}>{r.other}</td>
                  <td style={{ ...RIGHT, fontWeight: 600 }}>{r.close}</td>
                  <td style={{ ...RIGHT, color: r.chgColor }}>{r.chg}</td>
                  <td style={RIGHT}><span className={`tag ${r.coverCls}`}>{r.cover}</span></td>
                  <td><ShareBar w={r.w} bg={r.bg} share={r.share} /></td>
                </tr>
              ))}
              <tr className="total">
                <td>All counts</td>
                <td style={RIGHT}>{v.countTot.open}</td>
                <td style={RIGHT}>{v.countTot.prod}</td>
                <td style={RIGHT}>{v.countTot.disp}</td>
                <td style={RIGHT}>{v.countTot.other}</td>
                <td style={RIGHT}>{v.countTot.close}</td>
                <td style={{ ...RIGHT, color: v.countTot.chgColor }}>{v.countTot.chg}</td>
                <td style={RIGHT}>{v.countTot.cover}</td>
                <td />
              </tr>
            </tbody>
          </table>
        </div>
        <div className="pretty" style={{ fontSize: 12, color: 'var(--color-neutral-700)', marginTop: 12, lineHeight: 1.5, maxWidth: '100ch' }}>
          {v.countNote}
        </div>
      </Blueprint>

      {v.hasFlow ? (
        <Blueprint className="sheet" style={PANEL}>
          <PanelHead title="Produced against dispatched" right={
            <div style={{ display: 'flex', gap: 14, fontSize: 12, color: 'var(--color-neutral-800)' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <span style={{ width: 12, height: 12, display: 'block', background: 'var(--color-accent-500)' }} />Produced
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <span style={{ width: 12, height: 12, display: 'block', background: 'var(--color-neutral-800)' }} />Dispatched
              </span>
            </div>
          } />
          <Frame height={200} ticks={v.flowTicks} labels={v.dayLabels} labelGap={3}>
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'flex-end', gap: 3, padding: '0 4px' }}>
              {v.flowCols.map((c, i) => (
                <div key={i} title={c.tip} style={{ flex: '1 1 0', minWidth: 0, height: '100%', display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                  <div style={{ flex: 1, height: c.p, background: 'var(--color-accent-500)' }} />
                  <div style={{ flex: 1, height: c.d, background: 'var(--color-neutral-800)' }} />
                </div>
              ))}
            </div>
          </Frame>
        </Blueprint>
      ) : null}

      {v.checks.length ? (
        <Blueprint className="sheet" style={PANEL}>
          <PanelHead title="Register checks"
            hint="Days where a count's opening stock doesn't match the previous day's closing" />
          <div style={{ overflowX: 'auto', marginTop: 14 }}>
            <table className="table num" style={{ minWidth: 560 }}>
              <thead>
                <tr>
                  <th>Day</th><th>Count</th>
                  <th style={RIGHT}>Previous closing</th>
                  <th style={RIGHT}>Opening entered</th>
                  <th style={RIGHT}>Difference</th>
                </tr>
              </thead>
              <tbody>
                {v.checks.map((c, i) => (
                  <tr key={i}>
                    <td>{c.day}</td><td>{c.label}</td>
                    <td style={RIGHT}>{c.prev}</td>
                    <td style={RIGHT}>{c.open}</td>
                    <td style={{ ...RIGHT, fontWeight: 600 }}>{c.diff}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Blueprint>
      ) : null}
    </>
  )
}
